import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DeleteCommand, standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LeaseAgreementCategory } from '../../model/const/enums';
import { RentInvoicingOrderDetailsService } from '../../service/data/rent-invoicing/rent-invoicing-order-details.service';
import { LocalDate } from '../../../core/model/local-date';
import { MonthYearItem } from '../../../core/model/period';
import { getMonthName } from '../../../core/helper/month';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { BuildingService } from '../../service/data/building.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { EMPTY, exhaustMap, filter, finalize, interval, of, Subject, switchMap, take, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { Invoice } from '../../../invoice/model/invoice';
import { InvoiceListRentInvoicingOrderComponent } from '../../../invoice/component/invoice-list-rent-invoicing-order/invoice-list-rent-invoicing-order';
import { RentInvoicingOrderDetails } from '../../model/rent-invoicing/rent-invoicing-order-details';
import { RentInvoicingOrderStatus, RentInvoicingOrderStatusText } from '../../model/rent-invoicing/rent-invoicing-order-status';
import { RentInvoicingOrderCreateModel } from '../../model/rent-invoicing/rent-invoicing-order-create-model';
import { RentInvoicingOrderSettings } from '../../model/rent-invoicing/rent-invoicing-order-settings';
import { RentInvoicingOrderUpdateModel } from '../../model/rent-invoicing/rent-invoicing-order-update-model';

@Component({
    selector: 'app-rent-invoicing-order-form',
    templateUrl: './rent-invoicing-order-form.component.html',
    styleUrls: ['./rent-invoicing-order-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        FormCommandHandlerResolver,
        { provide: DataService, useExisting: RentInvoicingOrderDetailsService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentInvoicingOrderFormComponent extends FormComponentBase<RentInvoicingOrderDetails, RentInvoicingOrderDetailsService> {
  constructor(
    private _fb: FormBuilder,
    private _cf: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: RentInvoicingOrderDetailsService,
    private _messageService: MessageService,
    navigation: NavigationService,
    readonly companyService: CompanyService,
    readonly propertyService: PropertyService,
    readonly buildingService: BuildingService
  ) {
    super(activatedRoute, service, _messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.addDeleteMenu;
  }
  @ViewChild(InvoiceListRentInvoicingOrderComponent) invoiceList: InvoiceListRentInvoicingOrderComponent;

  protected createModel(): RentInvoicingOrderDetails {
    let model = new RentInvoicingOrderDetails();
    model.dueFrom = LocalDate.fromDate(LocalDate.today().firstDayInMonth());
    model.dueTo = LocalDate.fromDate(LocalDate.today().lastDayInMonth());
    return model;
  }

  public leaseAgreementCategorySelection = [
    { name: 'ALL_TEXT', value: null },
    { name: 'DASHBOARD.MENU.LEASE_AGREEMENT_DWELLING', value: LeaseAgreementCategory.Dwelling },
    { name: 'DASHBOARD.MENU.LEASE_AGREEMENT_PREMISES', value: LeaseAgreementCategory.Premises },
    { name: 'DASHBOARD.MENU.LEASE_AGREEMENT_PARKING', value: LeaseAgreementCategory.Parking },
    { name: 'DASHBOARD.MENU.LEASE_AGREEMENT_OTHER', value: LeaseAgreementCategory.Other },
    { name: 'DASHBOARD.MENU.LEASE_AGREEMENT_BLOCK', value: LeaseAgreementCategory.Block },
  ];

  protected getFormGroup(): FormGroup {
    const isReadonly = !(this.isNew || this.dataContext.invoiceCount === 0 || this.dataContext.status === RentInvoicingOrderStatus.Failed);
    return this._fb.group({
      caption: this._cf.create('RENT_INVOICING_ORDER.CAPTION', this.dataContext.caption),
      dueFrom: this._cf.createWithOptions(
        'RENT_INVOICING_ORDER.DUE_FROM',
        this.dataContext.dueFrom
          ? new MonthYearItem(this.dataContext.dueFrom.month, getMonthName(this.dataContext.dueFrom.month), this.dataContext.dueFrom.year)
          : null,
        { isReadonly: isReadonly }
      ),
      dueTo: this._cf.createWithOptions(
        'RENT_INVOICING_ORDER.DUE_TO',
        this.dataContext.dueTo
          ? new MonthYearItem(this.dataContext.dueTo.month, getMonthName(this.dataContext.dueTo.month), this.dataContext.dueTo.year)
          : null,
        { isReadonly: isReadonly }
      ),
      createdByName: this._cf.createReadonly('RENT_INVOICING_ORDER.CREATED_BY', this.dataContext.createdByName),
      createdAt: this._cf.createReadonly('RENT_INVOICING_ORDER.ORDERED_AT', this.dataContext.createdAt),
      completedAt: this._cf.createReadonly('RENT_INVOICING_ORDER.COMPLETED_AT', this.dataContext.completedAt),
      statusText: this._cf.createReadonly('RENT_INVOICING_ORDER.STATUS', RentInvoicingOrderStatusText(this.dataContext.status)),
      errorMessage: this._cf.createReadonly('RENT_INVOICING_ORDER.ERROR_MESSAGE', this.dataContext.errorMessage),
      invoiceCount: this._cf.createReadonly('RENT_INVOICING_ORDER.INVOICE_COUNT', this.dataContext.invoiceCount),
      attestedInvoiceCount: this._cf.createReadonly('RENT_INVOICING_ORDER.ATTEST_COUNT', this.dataContext.attestedInvoiceCount),
      leaseAgreementCaption: this._cf.createReadonly('COMMON.LEASE_AGREEMENT', this.dataContext.leaseAgreementCaption),
      appendixes: this._fb.array(this.dataContext.appendixes.map(f => this._cf.createHidden(f))),
      settings: this._fb.group({
        leaseAgreementCategory: this._cf.createWithOptions(
          'LEASE_AGREEMENT.TYPE_OF_LEASE_AGREEMENT',
          this.dataContext.settings.leaseAgreementCategory,
          { isReadonly: isReadonly }
        ),
        companyIds: this._cf.createWithOptions(
          'COMPANY.LIST_HEADER',
          this.dataContext.settings.companyIds ?? [],
          { isReadonly: isReadonly }
        ),
        propertyIds: this._cf.createWithOptions('COMMON.PROPERTY', this.dataContext.settings.propertyIds ?? [], {
          isReadonly: isReadonly,
        }),
        buildingIds: this._cf.createWithOptions('COMMON.BUILDING', this.dataContext.settings.buildingIds ?? [], {
          isReadonly: isReadonly,
        }),
        invoiceRowMessages: this._fb.array(
          (this.dataContext.settings.invoiceRowMessages ?? []).map(text => this._cf.createMaxLength('INVOICE.INVOICE_ROW_TEXT', text, 100))
        ),
      }),
    });
  }

  addTextRow(): void {
    this.textControls.controls.push(this._cf.createMaxLength('INVOICE.INVOICE_ROW_TEXT', '', 100));
  }

  deleteTextRow(textControl: FormControlEx): void {
    let index = this.textControls.controls.findIndex(x => x === textControl);
    if (index !== -1) {
      this.textControls.removeAt(index);
    }
  }

  get textControls(): FormArray {
    return this.settingsForm.controls['invoiceRowMessages'] as FormArray;
  }

  get appendixesControls(): FormArray {
    return this.form.controls['appendixes'] as FormArray;
  }

  get settingsForm(): FormGroup {
    return this.form.controls['settings'] as FormGroup;
  }

  get fromControl(): AbstractControl {
    return this.form.controls['dueFrom'];
  }
  get toControl(): AbstractControl {
    return this.form.controls['dueTo'];
  }

  moduleActive: boolean = true;
  periodSelection: MonthYearItem[] = [];
  mimeTypes: string[] = DocumentTypeMimeTypes.PDF();
  showInvoices: boolean = false;
  get showCompany(): boolean {
    return this.isNew || (this.settingsForm.controls['companyIds'].value?.length ?? 0) > 0;
  }
  get showProperty(): boolean {
    return this.isNew || (this.settingsForm.controls['propertyIds'].value?.length ?? 0) > 0;
  }
  get showBuilding(): boolean {
    return this.isNew || (this.settingsForm.controls['buildingIds'].value?.length ?? 0) > 0;
  }
  get showAttestAll(): boolean {
    return (
      this.dataContext.status == RentInvoicingOrderStatus.Unattested &&
      this.dataContext.invoiceCount > this.dataContext.attestedInvoiceCount
    );
  }
  get showCancelDelete(): boolean {
    return this.dataContext.attestedInvoiceCount === 0 && this.dataContext.status !== RentInvoicingOrderStatus.Pending;
  }
  get showLeaseAgreementCategory(): boolean {
    if (this.isNew) return true;
    let value = this.settingsForm.controls['leaseAgreementCategory'].value;
    return value || value == LeaseAgreementCategory.Dwelling;
  }

  working: boolean = false;
  cancelDelete() {
    if (this.working) return;
    this.working = true;
    this.form.markAsPristine();
    this.execute(new DeleteCommand(standardCommand.delete));
    this.working = false;
  }

  attestAll() {
    if (this.working) return;
    this.working = true;
    this.AddSubscription(
      this.dataService
        .attestAll(this.dataContext.id)
        .pipe(
          switchMap(r =>
            timer(0, 2000).pipe(
              exhaustMap(() => this.dataService.getPollingUpdate(this.dataContext.id)),
              tap(model => {
                this.updateView(model);
              }),
              takeWhile(
                model => model.status !== RentInvoicingOrderStatus.Completed && model.status !== RentInvoicingOrderStatus.Failed,
                true // emit last value
              )
            )
          ),
          finalize(() => {
            this.working = false;
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  get toggleStatsText(): string {
    return this.statsEnabled && this.hasStatistics ? 'RENT_INVOICING_ORDER.HIDE_STATISTICS' : 'RENT_INVOICING_ORDER.SHOW_STATISTICS';
  }

  statsEnabled: boolean = false;
  statsLoading: boolean = false;
  statsLoadAttempted: boolean = false;

  get canLoadStatistics(): boolean {
    return !!this.dataContext?.invoiceCount;
  }

  get hasStatistics(): boolean {
    return Array.isArray(this.previewDataContext) && this.previewDataContext.length > 0;
  }

  get showStatisticsEmptyState(): boolean {
    return this.statsLoadAttempted && !this.statsLoading && !this.hasStatistics;
  }

  toggleStats() {
    if (this.statsEnabled && this.hasStatistics) {
      this.statsEnabled = false;
      return;
    }

    this.loadInvoiceStatistics(true);
  }

  private loadInvoiceStatistics(showAfterLoad: boolean): void {
    if (!this.canLoadStatistics || this.statsLoading) return;

    this.statsLoading = true;
    this.statsLoadAttempted = true;
    this.AddSubscription(
      this.dataService
        .getInvoiceStatistics(this.dataContext.id)
        .pipe(
          finalize(() => {
            this.statsLoading = false;
          })
        )
        .subscribe({
          next: val => {
            this.previewDataContext = val ?? [];
            this.statsEnabled = showAfterLoad && this.hasStatistics;
          },
          error: error => {
            this.previewDataContext = [];
            this.statsEnabled = false;
            this._messageService.showError(error);
          },
        })
    );
  }

  protected init(data: RentInvoicingOrderDetails) {
    super.init(data);

    this.AddSubscription(
      this.fromControl.valueChanges.subscribe(value => {
        if (
          !this.toControl.value ||
          this.toControl.value.year < value.year ||
          (this.toControl.value.year == value.year && this.toControl.value.month < value.month)
        ) {
          this.toControl.setValue(value);
        }
      })
    );

    const companyIdsControl = this.settingsForm.controls['companyIds'];
    const propertyIdsControl = this.settingsForm.controls['propertyIds'];
    const buildingIdsControl = this.settingsForm.controls['buildingIds'];
    this.AddSubscription(
      companyIdsControl.valueChanges.subscribe(() => {
        propertyIdsControl.setValue([]);
      })
    );
    this.AddSubscription(
      propertyIdsControl.valueChanges.subscribe(() => {
        buildingIdsControl.setValue([]);
      })
    );

    if (this.isNew) {
      this.AddSubscription(
        this.dataService.getCreateData().subscribe(model => {
          this.moduleActive = model.invoicingModuleActive;
          this.periodSelection = model.invoicePeriods.map(p => new MonthYearItem(p.month, getMonthName(p.month), p.year));
          let period = this.periodSelection.find(p => p.month == model.defaultPeriod.month && p.year == model.defaultPeriod.year);
          this.fromControl.setValue(period);
          this.toControl.setValue(period);
        })
      );
      this.addTextRow();
    } else {
      this.periodSelection = data.invoicePeriods.map(p => new MonthYearItem(p.month, getMonthName(p.month), p.year));
      let from = this.periodSelection.find(p => p.month == data.dueFrom.month && p.year == data.dueFrom.year);
      this.fromControl.setValue(from);
      let to = this.periodSelection.find(p => p.month == data.dueTo.month && p.year == data.dueTo.year);
      this.toControl.setValue(to);

      if (data.invoiceCount) {
        this.showInvoices = true;
        this.loadInvoiceStatistics(false);
      }
      if (data.status === RentInvoicingOrderStatus.Pending) {
        this.AddSubscription(
          timer(0, 2000)
            .pipe(
              exhaustMap(() => this.dataService.getPollingUpdate(data.id)),
              tap(model => {
                this.updateView(model);
              }),
              takeWhile(
                model => model.status === RentInvoicingOrderStatus.Pending,
                true // emit last value
              ),
              switchMap(model => (model.invoiceCount ? this.dataService.getInvoiceStatistics(this.dataContext.id) : EMPTY))
            )
            .subscribe(stats => {
              this.showInvoices = true;
              this.previewDataContext = stats;
              this.statsLoadAttempted = true;
              this.statsEnabled = this.hasStatistics;
            })
        );
      }
    }
  }

  updateView(model: RentInvoicingOrderDetails) {
    this.form.patchValue({
      statusText: RentInvoicingOrderStatusText(model.status),
      completedAt: model.completedAt,
      attestedInvoiceCount: model.attestedInvoiceCount,
      invoiceCount: model.invoiceCount,
      errorMessage: model.errorMessage,
    });
    this.dataContext.invoiceCount = model.invoiceCount;
    this.dataContext.attestedInvoiceCount = model.attestedInvoiceCount;
    this.dataContext.status = model.status;
    if (this.showInvoices) this.invoiceList.reload();
  }

  previewDataContext: any[] = [];

  protected prepareCreate(): RentInvoicingOrderCreateModel {
    let model = new RentInvoicingOrderCreateModel();
    let form = this.form.value;

    model.appendixes = (this.form.controls['appendixes'] as FormArray).controls.map(c => c.value.data);
    model.caption = form.caption;
    model.dueFrom = this.fromControl.value;
    model.dueTo = this.toControl.value;
    model.settings = Object.assign(new RentInvoicingOrderSettings(), form.settings);
    model.settings.invoiceRowMessages = this.textControls.controls.map(c => c.value);
    model.settings.companyIds = model.settings.companyIds ?? [];

    return model;
  }

  private _retry: boolean = false;
  protected prepareUpdate(): RentInvoicingOrderUpdateModel {
    let form = this.form.value;
    let model = new RentInvoicingOrderUpdateModel();
    model.id = this.dataContext.id;
    model.caption = form.caption;
    model.dueFrom = this.fromControl.value;
    model.dueTo = this.toControl.value;
    model.settings = Object.assign(new RentInvoicingOrderSettings(), form.settings);
    model.settings.invoiceRowMessages = this.textControls.controls.map(c => c.value);
    model.settings.companyIds = model.settings.companyIds ?? [];
    model.retry = this._retry;
    return model;
  }

  onSubmit(): void {
    if (this.isSaving) return;
    this.form.markAsDirty();
    super.onSubmit();
  }

  retry(): void {
    if (this.isSaving || !this.form.valid) return;

    this._retry = true;
    this.onSubmit();
    this._retry = false;

    if (this.dataContext.status == RentInvoicingOrderStatus.Failed || this.dataContext.invoiceCount === 0) {
      // Wait for this.isSaving to become false before reloading.
      interval(100)
        .pipe(
          filter(() => !this.isSaving),
          take(1)
        )
        .subscribe({
          next: () => this.navigation.reloadCurrentRoute(),
          error: err => {
            console.error('Retry failed', err);
          },
        });
    }
  }
}
